import type { Player } from "@/lib/types";
import { PlayerAvatar } from "@/features/player-photos/PlayerAvatar";
import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useMatches } from "@/features/matches/useMatches";
import { useToast } from "@/components/ui/toast";
import { ackCoachingReviews, errorMessage } from "@/lib/api";
import { invalidateScope } from "@/lib/queryClient";
import {
  buildCoachFeedbackBoard,
  type CoachFeedbackBoardAction,
  type CoachFeedbackBoardCard,
  type CoachFeedbackBoardCardTone,
  type CoachFeedbackBoardLane,
} from "./coachFeedbackBoard";
import { PageHeader } from "./components/PageHeader";
import { MatchSummaryComposerModal } from "./components/MatchSummaryComposerModal";
import { useCoachEngagement } from "./hooks/useCoachEngagement";
import { useCoachGoalsList } from "./hooks/useCoachGoalsList";
import {
  useCoachSummariesList,
  useMatchSummaryMutations,
} from "./hooks/useCoachSummariesList";
import { useRoster } from "./hooks/useRoster";

/**
 * Coach Feedback Board — Phase 1 of the player-first coach
 * workspace. Bare `/coach` lands here, while the old Roster remains
 * available at `?tab=roster`.
 *
 * The board is intentionally a quiet triage surface, not a project
 * management tool: it answers "who needs attention?" and then links
 * into existing surfaces until the Player Feedback Home lands.
 */
export function CoachFeedbackBoardPage({
  onGiveFeedback,
}: {
  /** `CoachShell` has always accepted a player id here (it drives the
   * modal's recipient AND its `player` visibility default) — the board
   * was the only caller throwing it away, so a coach acting on a card
   * that names a player had to re-pick that player in the modal. */
  onGiveFeedback: (playerId?: string) => void;
}) {
  const navigate = useNavigate({ from: "/coach" });
  const rosterQuery = useRoster();
  const engagementQuery = useCoachEngagement({});
  const goalsQuery = useCoachGoalsList();
  const matchesQuery = useMatches();
  const summariesQuery = useCoachSummariesList();
  const toast = useToast();
  // Acknowledging a follow-up card is the only way to clear it: the card
  // is derived from the presence of a need_help / tried_this signal, so
  // before this the coach could read a request for help, act on it, and
  // still be nagged by it until the player edited their own response.
  const ackMutation = useMutation({
    mutationFn: ackCoachingReviews,
    onSuccess: () => invalidateScope(),
  });

  const handleAck = async (card: CoachFeedbackBoardCard) => {
    const targets = card.ackTargets ?? [];
    if (targets.length === 0) return;
    try {
      await ackMutation.mutateAsync(targets);
      toast.success("Marked seen");
    } catch (err) {
      toast.error("Couldn't mark it seen", { description: errorMessage(err) });
    }
  };
  const summaryMutations = useMatchSummaryMutations();
  const [recapMatchId, setRecapMatchId] = useState<string | null>(null);

  const board = useMemo(
    () =>
      buildCoachFeedbackBoard({
        players: rosterQuery.data?.players ?? [],
        engagement: engagementQuery.data,
        goals: goalsQuery.data?.goals ?? [],
        matches: matchesQuery.data ?? [],
        summaries: summariesQuery.data?.summaries ?? [],
      }),
    [
      engagementQuery.data,
      goalsQuery.data?.goals,
      matchesQuery.data,
      rosterQuery.data?.players,
      summariesQuery.data?.summaries,
    ],
  );

  const isLoading =
    rosterQuery.isLoading ||
    engagementQuery.isLoading ||
    goalsQuery.isLoading ||
    matchesQuery.isLoading ||
    summariesQuery.isLoading;
  const errors = [
    rosterQuery.error,
    engagementQuery.error,
    goalsQuery.error,
    matchesQuery.error,
    summariesQuery.error,
  ].filter(Boolean);

  const goToAction = (action: CoachFeedbackBoardAction) => {
    if (action.kind === "roster") {
      void navigate({
        to: "/coach",
        search: {
          tab: "roster",
          player_id: action.playerId,
          context: action.context,
        },
      });
      return;
    }
    if (action.kind === "notes") {
      void navigate({
        to: "/coach",
        search: {
          tab: "library",
          library: "notes",
          library_item_id: action.itemId,
        },
      });
      return;
    }
    if (action.kind === "clips") {
      void navigate({
        to: "/coach",
        search: {
          tab: "library",
          library: "clips",
          library_item_id: action.itemId,
        },
      });
      return;
    }
    if (action.kind === "recaps") {
      void navigate({
        to: "/coach",
        search: {
          tab: "library",
          library: "recaps",
          library_item_id: action.itemId,
        },
      });
      return;
    }
    setRecapMatchId(action.matchId ?? "");
  };

  return (
    <div className="flex flex-col gap-5" data-testid="coach-feedback-board">
      <PageHeader
        title="Team Feedback Board"
        eyebrow="Team follow-up"
        description="A quiet triage view for player follow-up, review completion, and match-level coaching gaps."
        actions={
          <Button type="button" onClick={() => onGiveFeedback()} size="sm">
            Give feedback
          </Button>
        }
      />

      <section
        aria-label="Feedback attention summary"
        className="grid grid-cols-2 gap-3 lg:grid-cols-4"
        data-testid="coach-feedback-board-summary"
      >
        <SummaryTile label="Needs touch" value={board.summary.needsTouch} />
        <SummaryTile label="Follow-up" value={board.summary.followUp} />
        <SummaryTile label="Unreviewed" value={board.summary.unreviewed} />
        <SummaryTile
          label="Recaps needed"
          value={board.summary.missingRecaps}
        />
      </section>

      {errors.length > 0 ? (
        <div
          className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-ink"
          data-testid="coach-feedback-board-error"
        >
          Some board data could not load:{" "}
          <span className="text-ink-muted">
            {errors.map((err) => errorMessage(err)).join(" · ")}
          </span>
        </div>
      ) : null}

      {isLoading ? (
        <p
          className="px-3 py-6 font-mono text-sm text-ink-muted"
          data-testid="coach-feedback-board-loading"
        >
          Loading feedback board…
        </p>
      ) : (
        <section
          aria-label="Feedback attention lanes"
          className="grid gap-3 xl:grid-cols-4"
          data-testid="coach-feedback-board-lanes"
        >
          {board.lanes.map((lane) => (
            <BoardLane
              key={lane.id}
              lane={lane}
              players={rosterQuery.data?.players ?? []}
              onAck={handleAck}
              onAction={goToAction}
              onGiveFeedback={onGiveFeedback}
            />
          ))}
        </section>
      )}
      {recapMatchId !== null ? (
        <MatchSummaryComposerModal
          summary={null}
          initialMatchId={recapMatchId || undefined}
          mutations={summaryMutations}
          onClose={() => setRecapMatchId(null)}
        />
      ) : null}
    </div>
  );
}

function SummaryTile({ label, value }: { label: string; value: number }) {
  return (
    <Card padded={false} className="px-3 py-2">
      <div className="font-display text-2xl leading-none text-ink">{value}</div>
      <div className="eyebrow mt-1">{label}</div>
    </Card>
  );
}

function BoardLane({
  lane,
  players,
  onAck,
  onAction,
  onGiveFeedback,
}: {
  lane: CoachFeedbackBoardLane;
  players: Player[];
  onAck: (card: CoachFeedbackBoardCard) => void;
  onAction: (action: CoachFeedbackBoardAction) => void;
  onGiveFeedback: (playerId?: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <section
      className="min-w-0 rounded-panel border border-rule bg-surface shadow-rest"
      data-testid={`coach-feedback-board-lane-${lane.id}`}
    >
      <header className="board-lane-heading border-b border-rule px-3 py-2.5">
        <div className="min-w-0">
          <h3 className="text-base font-display text-ink">{lane.title}</h3>
        </div>
        <Badge variant={lane.cards.length > 0 ? "accent" : "muted"}>
          {lane.cards.length}
        </Badge>
        <p className="text-xs text-ink-muted">{lane.description}</p>
      </header>
      <div className="flex flex-col gap-2 p-2">
        {lane.cards.length === 0 ? (
          <p className="rounded-inset border border-dashed border-rule bg-field px-3 py-3 text-sm text-ink-muted">
            Nothing needs attention here right now.
          </p>
        ) : (
          (expanded ? lane.cards : lane.cards.slice(0, 3)).map((card) => (
            <BoardCard
              key={card.id}
              card={card}
              player={players.find((p) => p.id === card.playerId)}
              onAck={onAck}
              onAction={onAction}
              onGiveFeedback={onGiveFeedback}
            />
          ))
        )}
      </div>
      {lane.cards.length > 3 ? (
        <Button
          variant="ghost"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {expanded ? "Show fewer" : `View all ${lane.cards.length}`}
        </Button>
      ) : null}
    </section>
  );
}

function BoardCard({
  card,
  player,
  onAck,
  onAction,
  onGiveFeedback,
}: {
  card: CoachFeedbackBoardCard;
  player?: Player;
  onAck: (card: CoachFeedbackBoardCard) => void;
  onAction: (action: CoachFeedbackBoardAction) => void;
  onGiveFeedback: (playerId?: string) => void;
}) {
  return (
    <Card
      as="article"
      tone={toneBar(card.tone)}
      padded={false}
      className="bg-canvas py-2.5 pr-3 pl-[calc(theme(spacing.3)+var(--tone-bar))]"
      data-testid="coach-feedback-board-card"
      data-board-card-id={card.id}
      data-lane={card.lane}
    >
      <div className="board-card-meta flex items-center justify-between gap-2">
        <p className="min-w-0 text-xs text-ink-muted">{card.meta}</p>
        <Badge variant={toneBadgeVariant(card.tone)}>
          {toneLabel(card.tone)}
        </Badge>
      </div>
      <div className="board-card-identity mt-3 flex items-center gap-3">
        {player && card.lane !== "shared" ? (
          <PlayerAvatar player={player} size="lg" />
        ) : null}
        <div className="min-w-0">
          <h4 className="font-display text-ink">
            {player && card.lane !== "shared"
              ? player.display_name
              : card.title}
          </h4>
          {player && card.lane !== "shared" && player.position ? (
            <span className="text-sm text-ink-muted">{player.position}</span>
          ) : null}
        </div>
      </div>
      {player && card.lane === "shared" ? (
        <div className="board-card-recipient mt-3 flex items-center gap-2">
          <PlayerAvatar player={player} size="sm" />
          <span className="text-xs">{player.display_name}</span>
        </div>
      ) : null}
      <p className="mt-2 text-sm leading-5 text-ink-muted">
        {card.description}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {/* The board answers "who needs attention?", so the card that
         * names a player should lead straight into writing for THAT
         * player — the primary action here, with the read-only detour
         * (open the item / view the roster row) kept as the secondary.
         * Before this, acting on a card meant re-picking the player the
         * card had just named. */}
        {card.playerId ? (
          <Button
            type="button"
            onClick={() => onGiveFeedback(card.playerId)}
            size="sm"
            data-testid="coach-feedback-board-card-give-feedback"
          >
            Give feedback
          </Button>
        ) : null}
        <Button
          type="button"
          onClick={() => onAction(card.action)}
          variant="outline"
          size="sm"
        >
          {actionLabel(card.action)}
        </Button>
        {/* Only response-derived cards can be acknowledged — the rest of
         * the board is derived from item state, which acking would not
         * change. Marks the whole group seen: the card showed the
         * player's responses together, so it clears them together. */}
        {card.ackTargets?.length ? (
          <Button
            type="button"
            onClick={() => onAck(card)}
            variant="ghost"
            size="sm"
            data-testid="coach-feedback-board-card-ack"
          >
            Mark seen
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

// Map a board card's tone to the Card primitive's 3px triage bar. The
// board's "accent" tone (a new, unactioned item) shows the cyan/neutral
// bar; the default tone shows no bar.
function toneBar(
  tone: CoachFeedbackBoardCardTone,
): "good" | "flag" | "neutral" | undefined {
  if (tone === "flag") return "flag";
  if (tone === "good") return "good";
  if (tone === "accent") return "neutral";
  return undefined;
}

function actionLabel(action: CoachFeedbackBoardAction): string {
  // Every kind is named. The old fallthrough labelled `clips` and
  // `recaps` "Write recap" even though both navigate to an EXISTING
  // library item — only `summaries` opens the recap composer.
  switch (action.kind) {
    case "roster":
      return "View player";
    case "notes":
      return "Open note";
    case "clips":
      return "Open clip";
    case "recaps":
      return "Open recap";
    case "summaries":
      return "Write recap";
  }
}

function toneBadgeVariant(
  tone: CoachFeedbackBoardCardTone,
): "accent" | "good" | "flag" | "muted" {
  if (tone === "flag") return "flag";
  if (tone === "good") return "good";
  if (tone === "accent") return "accent";
  return "muted";
}

function toneLabel(tone: CoachFeedbackBoardCardTone): string {
  if (tone === "flag") return "Flag";
  if (tone === "good") return "Good";
  if (tone === "accent") return "New";
  return "Review";
}
